/**
 * ApplicationContext - Single source of truth for application state
 * 
 * Consolidates all state management, types, reducer, and context in one file.
 * Exposes individual state properties (not a giant state object) for better
 * component optimization and clearer dependencies.
 */

import React, { createContext, useContext, useMemo, useCallback, useReducer, ReactNode } from "react";
import { getModuleFromTenant, pruneRxLiteMenu } from "../utils/landingAccess";
import { getSessionId, getAuthType } from "../api";
import { setCurrentPlatform as persistCurrentPlatform } from "../security/authStorage";
import { createLogger } from '../utils/logging';
import type { Account } from "../api/types";

const logger = createLogger('ApplicationContext');

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ViewType = "landing" | "iframe" | "component";
// Only "home" (landing) and "rxLite" (component) are still checked anywhere;
// "other" is the placeholder used for all iframe views, which have no
// per-vertical landing page to route back to.
export type AppSection = "home" | "rxLite" | "other";

export interface ActiveView {
  type: ViewType;
  section: AppSection;
  url?: string;
  component?: string;
}

export interface AppHeaderState {
  activeView: ActiveView;
  activeNavItemId: string | null;

  // Filtered vertical navigation (ability-filtered)
  filteredNavigation: any[];  // NavItem[] - full filtered nav tree
  filteredVerticalSections: Record<string, any>;  // Legacy format for backward compat

  // Menu data arrays
  abilities: string[];
  abilitiesLoaded: boolean; // Tracks if abilities have been fetched (true even if empty)
  rxLiteMenu: any[];

  // Section visibility (unified map for main navigation)
  sectionVisibility: Record<string, boolean>;
  
  // Legacy rxLite specific flags (still used by ContentArea)
  showHideRxClaimLiteClaims: boolean;
  showHideRxClaimLiteMember: boolean;

  // User & authentication state
  displayUser: string;
  userAliasName: string;
  liteMemRbac: string;
  liteClaimsRbac: string;

  // Tenant management
  tenantId: string;
  currentProgram: string;
  prestoWindow: any;
  tenantIds: string[];
  failedTenants: string[];
  tenantStack: string[];
  currentTenant: string;

  // OHID account management
  accounts: Account[];
  currentAccount: string;

  // Platform management
  platforms: string[];
  currentPlatform: string;
  // Platform -> tenant/instance lookup (OHID), built from the platform-list platform-tenants map
  platformTenantMap: Record<string, string>;

  // UI configuration
  logoSrc: string;

  // Business data
  providerResults: any;
  operationResults: any;
  modalOpen: boolean;
  // Which selector triggered the confirm modal, and the value awaiting confirmation
  switchKind: "tenant" | "account" | "platform" | null;
  pendingSwitchValue: string;
  isPriceFrame: boolean;
  programData?: any;
  latestHelp?: { helpPage?: string; application?: string; programName?: string; isPriceFrame?: boolean };
}

export type AppHeaderAction =
  // Navigation
  | { type: "UPDATE_FRAME"; url?: string; toggleMenu?: boolean; programData?: any; preserveSection?: boolean }
  | { type: "SET_HOME" }
  | { type: "SET_LANDING"; section: AppSection }
  | { type: "SET_COMPONENT"; component: "claims" | "member" }
  | { type: "SET_ACTIVE_VIEW"; activeView: ActiveView }
  | { type: "SET_ACTIVE_NAV_ITEM"; itemId: string | null }

  // Menu data
  | { type: "SET_ABILITIES"; abilities: string[] }
  | { type: "SET_ABILITIES_LOADED"; loaded: boolean }
  | { type: "SET_RXLITE_MENU"; menu: string[] }
  | { type: "SET_FILTERED_NAVIGATION"; navigation: any[] }
  | { type: "SET_FILTERED_VERTICAL_SECTIONS"; sections: Record<string, any> }

  // Menu visibility
  | { type: "SET_MENU_VISIBILITY"; section: string; visible: boolean }

  // User
  | { type: "SET_USER"; displayUser: string; userAliasName: string }
  | { type: "SET_LITE_RBAC"; claimsRbac: string; memberRbac: string }

  // Tenant
  | { type: "UPDATE_TENANT"; tenantId: string }
  | { type: "SET_CURRENT_TENANT"; tenantId: string }
  | { type: "SET_PENDING_TENANT"; tenantId: string }
  | { type: "SET_TENANT_IDS"; tenantIds: string[] }
  | { type: "SET_FAILED_TENANTS"; failedTenants: string[] }
  | { type: "PUSH_TENANT_STACK"; tenantId: string }
  | { type: "POP_TENANT_STACK" }
  | { type: "SET_CURRENT_PROGRAM"; program: string }
  | { type: "SET_PRESTO_WINDOW"; prestoWindow: Window | null }

  // Account (OHID)
  | { type: "SET_ACCOUNTS"; accounts: Account[] }
  | { type: "SET_CURRENT_ACCOUNT"; account: string }

  // Platform
  | { type: "SET_PLATFORMS"; platforms: string[] }
  | { type: "SET_CURRENT_PLATFORM"; platform: string }
  | { type: "SET_PLATFORM_TENANT_MAP"; map: Record<string, string> }

  // Modals
  | { type: "OPEN_SWITCH_MODAL"; switchKind: "tenant" | "account" | "platform"; pendingValue: string }
  | { type: "CLOSE_MODAL" }

  // UI state
  | { type: "SET_IS_PRICE_FRAME"; isPriceFrame: boolean }
  | { type: "SET_PROGRAM_DATA"; data: any }
  | { type: "SET_LATEST_HELP"; latestHelp: { helpPage?: string; application?: string; programName?: string; isPriceFrame?: boolean } }

  // Batch operations
  | { type: "CHECK_LANDING_PAGE_ACCESS"; tenant: string };

// ============================================================================
// REDUCER FUNCTION
// ============================================================================

function appHeaderReducer(state: AppHeaderState, action: AppHeaderAction): AppHeaderState {
  switch (action.type) {
    // ====== NAVIGATION ======
    case "SET_HOME":
      return {
        ...state,
        activeView: { type: "landing", section: "home" },
        activeNavItemId: null,
      };

    case "SET_LANDING":
      return {
        ...state,
        activeView: { type: "landing", section: action.section },
      };

    case "SET_COMPONENT":
      return {
        ...state,
        activeView: { type: "component", section: "rxLite", component: action.component },
      };

    case "SET_ACTIVE_VIEW":
      return {
        ...state,
        activeView: action.activeView,
      };

    case "SET_ACTIVE_NAV_ITEM":
      return {
        ...state,
        activeNavItemId: action.itemId,
      };

    case "UPDATE_FRAME": {
      // `section` is retained on ActiveView for the landing/component views
      // ("home", "rxLite"), but iframe views have no per-vertical landing
      // page to route back to, so there's nothing meaningful to classify
      // the URL into. Kept as "other" for type compatibility.
      return {
        ...state,
        activeView: { type: "iframe", section: "other", url: action.url || "" },
        programData: (action as any).programData !== undefined ? (action as any).programData : state.programData,
      };
    }

    // ====== MENU DATA ======
    case "SET_ABILITIES":
      return {
        ...state,
        abilities: action.abilities,
      };

    case "SET_ABILITIES_LOADED":
      return {
        ...state,
        abilitiesLoaded: action.loaded,
      };

    case "SET_RXLITE_MENU":
      {
        const showClaims = action.menu.includes("Claims Search");
        const showMember = action.menu.includes("Member Search");
      return {
        ...state,
        rxLiteMenu: action.menu,
        showHideRxClaimLiteClaims: showClaims,
        showHideRxClaimLiteMember: showMember,
      };
      }

    case "SET_FILTERED_NAVIGATION":
      return {
        ...state,
        filteredNavigation: action.navigation,
      };

    case "SET_FILTERED_VERTICAL_SECTIONS":
      return {
        ...state,
        filteredVerticalSections: action.sections,
      };

    case "SET_MENU_VISIBILITY":
      return {
        ...state,
        sectionVisibility: {
          ...state.sectionVisibility,
          [action.section]: action.visible,
        },
      };

    // ====== USER ======
    case "SET_USER":
      return {
        ...state,
        displayUser: action.displayUser,
        userAliasName: action.userAliasName,
      };

    case "SET_LITE_RBAC":
      return {
        ...state,
        liteClaimsRbac: action.claimsRbac,
        liteMemRbac: action.memberRbac,
      };

    // ====== TENANT ======
    case "UPDATE_TENANT":
      return {
        ...state,
        tenantId: action.tenantId,
      };

    case "SET_CURRENT_TENANT":
      return {
        ...state,
        currentTenant: action.tenantId,
      };

    case "SET_TENANT_IDS":
      return {
        ...state,
        tenantIds: action.tenantIds,
      };

    case "SET_FAILED_TENANTS":
      return {
        ...state,
        failedTenants: action.failedTenants,
      };

    case "PUSH_TENANT_STACK":
      return {
        ...state,
        tenantStack: [...state.tenantStack, action.tenantId],
      };

    case "POP_TENANT_STACK":
      return {
        ...state,
        tenantStack: state.tenantStack.slice(0, -1),
      };

    case "SET_CURRENT_PROGRAM":
      return {
        ...state,
        currentProgram: action.program,
      };

    case "SET_PRESTO_WINDOW":
      return {
        ...state,
        prestoWindow: action.prestoWindow,
      };

    // ====== ACCOUNT (OHID) ======
    case "SET_ACCOUNTS":
      return {
        ...state,
        accounts: action.accounts,
      };

    case "SET_CURRENT_ACCOUNT":
      return {
        ...state,
        currentAccount: action.account,
      };

    // ====== PLATFORM ======
    case "SET_PLATFORMS":
      return {
        ...state,
        platforms: action.platforms,
      };

    case "SET_CURRENT_PLATFORM":
      return {
        ...state,
        currentPlatform: action.platform,
      };

    case "SET_PLATFORM_TENANT_MAP":
      return {
        ...state,
        platformTenantMap: action.map,
      };

    // ====== MODALS ======
    case "OPEN_SWITCH_MODAL":
      return {
        ...state,
        modalOpen: true,
        switchKind: action.switchKind,
        pendingSwitchValue: action.pendingValue,
      };

    case "CLOSE_MODAL":
      return {
        ...state,
        modalOpen: false,
      };

    // ====== UI STATE ======
    case "SET_IS_PRICE_FRAME":
      return {
        ...state,
        isPriceFrame: action.isPriceFrame,
      };

    case "SET_PROGRAM_DATA":
      return {
        ...state,
        programData: action.data,
      };

    case "SET_LATEST_HELP":
      return {
        ...state,
        latestHelp: action.latestHelp,
      };

    // ====== BATCH OPERATIONS ======
    case "CHECK_LANDING_PAGE_ACCESS": {
      const module = getModuleFromTenant(action.tenant);
      const { menu, showClaims, showMember } = pruneRxLiteMenu(
        state.rxLiteMenu,
        state.abilities,
        module,
      );

      let updates: Partial<AppHeaderState> = {};

      // Tenant switch always returns the user to Home. The iframe holds
      // tenant-scoped state that must be reloaded fresh under the new tenant,
      // so there is no safe way to keep the user on their current section/page.
      const alreadyHome = state.activeView.type === "landing" && state.activeView.section === "home";
      if (!alreadyHome) {
        updates.activeView = { type: "landing", section: "home" };
      }

      if (menu.length !== state.rxLiteMenu.length) {
        updates.rxLiteMenu = menu;
        updates.showHideRxClaimLiteClaims = showClaims;
        updates.showHideRxClaimLiteMember = showMember;
      } else if (
        state.showHideRxClaimLiteClaims !== showClaims ||
        state.showHideRxClaimLiteMember !== showMember
      ) {
        updates.showHideRxClaimLiteClaims = showClaims;
        updates.showHideRxClaimLiteMember = showMember;
      }

      return {
        ...state,
        ...updates,
      };
    }

    default:
      return state;
  }
}

// ============================================================================
// INITIAL STATE
// ============================================================================

function createInitialState(): AppHeaderState {
  return {
    activeView: { type: "landing", section: "home" },
    activeNavItemId: null,
    filteredNavigation: [],
    filteredVerticalSections: {},

    abilities: [],
    abilitiesLoaded: false,
    rxLiteMenu: ["Claims Search", "Member Search"],

    // Section visibility (unified map)
    sectionVisibility: {},
    
    // Legacy rxLite specific flags
    showHideRxClaimLiteClaims: false,
    showHideRxClaimLiteMember: false,

    displayUser: "",
    userAliasName: "",
    liteMemRbac: "",
    liteClaimsRbac: "",

    tenantId: "",
    currentProgram: "",
    prestoWindow: null,
    tenantIds: [],
    failedTenants: [],
    tenantStack: [],
    currentTenant: "",

    accounts: [],
    currentAccount: "",

    platforms: [],
    currentPlatform: "",
    platformTenantMap: {},

    logoSrc: "",

    providerResults: null,
    operationResults: null,
    modalOpen: false,
    switchKind: null,
    pendingSwitchValue: "",
    isPriceFrame: false,
    programData: null,
    latestHelp: undefined,
  };
}

// ============================================================================
// CONTEXT API INTERFACE
// ============================================================================

/**
 * ApplicationContainerAPI - Exposes individual state properties + action creators
 * 
 * Components destructure only what they need:
 *   const { activeView, abilities, navigateToLanding } = useApplicationContext();
 * 
 * This is better than passing entire state object because:
 * - Components only subscribe to properties they use
 * - More explicit dependencies
 * - Easier to understand component needs
 * - Better for future optimizations
 */
export interface ApplicationContainerAPI {
  // Individual state properties (NOT state object)
  activeView: ActiveView;
  activeNavItemId: string | null;
  filteredNavigation: any[];
  filteredVerticalSections: Record<string, any>;
  abilities: string[];
  abilitiesLoaded: boolean;
  rxLiteMenu: any[];
  
  // Section visibility (unified map for main navigation)
  sectionVisibility: Record<string, boolean>,
  
  // Legacy rxLite specific flags (still used by ContentArea)
  showHideRxClaimLiteClaims: boolean;
  showHideRxClaimLiteMember: boolean;
  displayUser: string;
  userAliasName: string;
  liteMemRbac: string;
  liteClaimsRbac: string;
  tenantId: string;
  currentProgram: string;
  prestoWindow: any;
  tenantIds: string[];
  failedTenants: string[];
  tenantStack: string[];
  currentTenant: string;
  accounts: Account[];
  currentAccount: string;
  platforms: string[];
  currentPlatform: string;
  platformTenantMap: Record<string, string>;
  logoSrc: string;
  providerResults: any;
  operationResults: any;
  modalOpen: boolean;
  switchKind: "tenant" | "account" | "platform" | null;
  pendingSwitchValue: string;
  isPriceFrame: boolean;
  programData?: any;
  latestHelp?: { helpPage?: string; application?: string; programName?: string; isPriceFrame?: boolean };

  // Action creators
  dispatch: React.Dispatch<AppHeaderAction>;
  navigateToLanding: (section: string) => void;
  navigateToSection: (url: string, toggleMenu?: boolean, programData?: any, preserveSection?: boolean) => void;
  navigateToComponent: (component: "claims" | "member") => void;
  updateTenant: (tenantId: string) => void;
  setCurrentTenant: (tenantId: string) => void;
  setTenantIds: (tenantIds: string[]) => void;
  setAccounts: (accounts: Account[]) => void;
  setCurrentAccount: (account: string) => void;
  setPlatforms: (platforms: string[]) => void;
  setCurrentPlatform: (platform: string) => void;
  setPlatformTenantMap: (map: Record<string, string>) => void;
  setUser: (displayUser: string, userAliasName: string) => void;
  setAbilities: (abilities: string[]) => void;
  setAbilitiesLoaded: (loaded: boolean) => void;
  setFilteredNavigation: (navigation: any[]) => void;
  setFilteredVerticalSections: (sections: Record<string, any>) => void;
  setMenuVisibility: (section: string, visible: boolean) => void;
  setActiveNavItem: (itemId: string | null) => void;
  setCurrentProgram: (program: string) => void;
  setProgramData: (data: any) => void;
  setLatestHelp: (latestHelp: { helpPage?: string; application?: string; programName?: string; isPriceFrame?: boolean }) => void;
  openHelp: () => void;
  setRxLiteMenu: (menu: string[]) => void;
  setLiteRbac: (claimsRbac: string, memberRbac: string) => void;
  setPrestoWindow: (prestoWindow: Window | null) => void;
  pushTenantStack: (tenantId: string) => void;
  popTenantStack: () => void;
  openSwitchModal: (switchKind: "tenant" | "account" | "platform", pendingValue: string) => void;
  closeModal: () => void;
  checkLandingPageAccess: (tenant: string) => void;
  setHome: () => void;
  openPresto: (prestoURLParams?: string) => void;
  getContextString: () => { sid: string; authtype: string; tenantId: string; rbac: string };
  getContextStringLite: () => { sid: string; authtype: string; tenantId: string; rbac: string; claimsRbac: string };
}

// ============================================================================
// CONTEXT & PROVIDER
// ============================================================================

export const ApplicationContext = createContext<ApplicationContainerAPI | undefined>(undefined);

/**
 * ApplicationProvider - Single source of truth for application state
 * 
 * Contains the useReducer hook directly (proper Context pattern).
 * Exposes individual state properties (NOT entire state object).
 * This allows components to destructure only what they need.
 */
export const ApplicationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appHeaderReducer, undefined, createInitialState);

  // Memoize all action creators to ensure stable references
  const navigateToLanding = useCallback((section: string) => {
    dispatch({ type: "SET_LANDING", section: section as any });
  }, []);

  const navigateToSection = useCallback((url: string, toggleMenu = true, programData?: any, preserveSection = false) => {
    dispatch({ type: "UPDATE_FRAME", url, toggleMenu, programData, preserveSection });
  }, []);

  const navigateToComponent = useCallback((component: "claims" | "member") => {
    dispatch({ type: "SET_COMPONENT", component });
  }, []);

  const updateTenant = useCallback((tenantId: string) => {
    dispatch({ type: "UPDATE_TENANT", tenantId });
    try {
      sessionStorage.setItem("tenantId", tenantId);
    } catch (e) {
      // ignore sessionStorage errors
    }
  }, []);

  const setCurrentTenant = useCallback((tenantId: string) => {
    dispatch({ type: "SET_CURRENT_TENANT", tenantId });
    try {
      sessionStorage.setItem("tenantId", tenantId);
    } catch (e) {
      // ignore sessionStorage errors
    }
  }, []);

  const setTenantIds = useCallback((tenantIds: string[]) => {
    dispatch({ type: "SET_TENANT_IDS", tenantIds });
  }, []);

  const setAccounts = useCallback((accounts: Account[]) => {
    dispatch({ type: "SET_ACCOUNTS", accounts });
  }, []);

  const setCurrentAccount = useCallback((account: string) => {
    dispatch({ type: "SET_CURRENT_ACCOUNT", account });
  }, []);

  const setPlatforms = useCallback((platforms: string[]) => {
    dispatch({ type: "SET_PLATFORMS", platforms });
  }, []);

  const setCurrentPlatform = useCallback((platform: string) => {
    persistCurrentPlatform(platform);
    dispatch({ type: "SET_CURRENT_PLATFORM", platform });
  }, []);

  const setPlatformTenantMap = useCallback((map: Record<string, string>) => {
    dispatch({ type: "SET_PLATFORM_TENANT_MAP", map });
  }, []);

  const setUser = useCallback((displayUser: string, userAliasName: string) => {
    dispatch({ type: "SET_USER", displayUser, userAliasName });
  }, []);

  const setAbilities = useCallback((abilities: string[]) => {
    dispatch({ type: "SET_ABILITIES", abilities });
  }, []);

  const setAbilitiesLoaded = useCallback((loaded: boolean) => {
    dispatch({ type: "SET_ABILITIES_LOADED", loaded });
  }, []);

  const setFilteredNavigation = useCallback((navigation: any[]) => {
    dispatch({ type: "SET_FILTERED_NAVIGATION", navigation });
  }, []);

  const setFilteredVerticalSections = useCallback((sections: Record<string, any>) => {
    dispatch({ type: "SET_FILTERED_VERTICAL_SECTIONS", sections });
  }, []);

  const setMenuVisibility = useCallback((section: string, visible: boolean) => {
    dispatch({ type: "SET_MENU_VISIBILITY", section, visible });
  }, []);

  const setActiveNavItem = useCallback((itemId: string | null) => {
    dispatch({ type: "SET_ACTIVE_NAV_ITEM", itemId });
  }, []);

  const setCurrentProgram = useCallback((program: string) => {
    dispatch({ type: "SET_CURRENT_PROGRAM", program });
  }, []);

  const setProgramData = useCallback((data: any) => {
    dispatch({ type: "SET_PROGRAM_DATA", data });
  }, [])
  ;
  const setLatestHelp = useCallback((latestHelp: { helpPage?: string; application?: string; programName?: string; isPriceFrame?: boolean }) => {
    dispatch({ type: "SET_LATEST_HELP", latestHelp });
  }, []);

  const openHelp = useCallback(() => {
    const lh = state.latestHelp as { helpPage?: string; application?: string; programName?: string; isPriceFrame?: boolean } | undefined;
    if (lh && lh.helpPage) {
      window.open(lh.helpPage, "Help");
      return;
    }
    if (lh && lh.application && lh.programName) {
      const helpPage = lh.isPriceFrame
        ? "/help/NextGen_Price/index.htm#t=" + lh.application + "%2F" + lh.programName + ".htm"
        : "/help/index.htm#t=" + lh.application + "%2F" + lh.programName + ".htm";
      window.open(helpPage, "Help");
      return;
    }
    window.open("/help/index.htm#t=Misc%2FWelcomeRxC_NextGen.htm", "Help");
  }, [state.latestHelp]);

  const setPrestoWindow = useCallback((prestoWindow: Window | null) => {
    dispatch({ type: "SET_PRESTO_WINDOW", prestoWindow });
  }, []);

  const pushTenantStack = useCallback((tenantId: string) => {
    dispatch({ type: "PUSH_TENANT_STACK", tenantId });
  }, []);

  const popTenantStack = useCallback(() => {
    dispatch({ type: "POP_TENANT_STACK" });
  }, []);

  const setRxLiteMenu = useCallback((menu: string[]) => {
    dispatch({ type: "SET_RXLITE_MENU", menu });
  }, []);

  const setLiteRbac = useCallback((claimsRbac: string, memberRbac: string) => {
    dispatch({ type: "SET_LITE_RBAC", claimsRbac, memberRbac });
  }, []);

  const openSwitchModal = useCallback((switchKind: "tenant" | "account" | "platform", pendingValue: string) => {
    dispatch({ type: "OPEN_SWITCH_MODAL", switchKind, pendingValue });
  }, []);

  const closeModal = useCallback(() => {
    dispatch({ type: "CLOSE_MODAL" });
  }, []);

  const checkLandingPageAccess = useCallback((tenant: string) => {
    dispatch({ type: "CHECK_LANDING_PAGE_ACCESS", tenant });
  }, []);

  const setHome = useCallback(() => {
    dispatch({ type: "SET_HOME" });
  }, []);

  /**
   * Open RxClaim Web (Presto) in a new window
   */
  const openPresto = useCallback((prestoURLParams?: string) => {
    const env = (window as any)["environments"];
    let authType = getAuthType();

    // If security is disabled use msid
    if (authType === "testing") {
      authType = "msid";
    }

    let urlParms = "";
    // If Presto URL parameters add to URL
    if (prestoURLParams) {
      (window as any).sharedParameters = prestoURLParams;
      urlParms = "&pgm=" + state.currentProgram;
    }

    try {
      const openURL =
        env.tenant[state.tenantId][authType].presto +
        "?tenant=" +
        state.tenantId +
        urlParms;
      logger.debug('Open Presto', 'Opening RxClaim Web window', { openURL, tenantId: state.tenantId });
      const prestoWindow = window.open(openURL, "RxClaimWeb");
      dispatch({ type: "SET_PRESTO_WINDOW", prestoWindow });
    } catch (error) {
      alert(
        "Error occurred while opening RxClaim Web. Please contact the support team."
      );
      logger.error('Presto Open Failed', 'Unable to open RxClaim Web window', { error, tenantId: state.tenantId });
    }
  }, [state.currentProgram, state.tenantId]);

  /**
   * Get context string for Claims webcomponent
   * Provides session ID, auth type, tenant ID, and RBAC permissions
   */
  const getContextString = useCallback(() => {
    return {
      sid: getSessionId() || '',
      authtype: getAuthType() || '',
      tenantId: state.tenantId || sessionStorage.getItem("tenantId") || "",
      rbac: state.liteClaimsRbac || "",
    };
  }, [state.tenantId, state.liteClaimsRbac]);

  /**
   * Get context string for Member webcomponent
   * Includes both member RBAC and claims RBAC
   */
  const getContextStringLite = useCallback(() => {
    return {
      sid: getSessionId() || '',
      authtype: getAuthType() || '',
      tenantId: state.tenantId || sessionStorage.getItem("tenantId") || "",
      rbac: state.liteMemRbac || "",
      claimsRbac: state.liteClaimsRbac || "",
    };
  }, [state.tenantId, state.liteMemRbac, state.liteClaimsRbac]);

  /**
   * Context value exposes individual state properties + action creators
   * Components destructure only what they need:
   *   const { activeView, abilities, navigateToLanding } = useApplicationContext();
   * 
   * Benefits vs passing entire state object:
   * - Clearer component dependencies
   * - Components only subscribe to properties they use
   * - Better for optimization and understanding
   */
  const contextValue = useMemo(() => {
    return {
      // Explicit state fields (avoid spreading entire state object)
      activeView: state.activeView,
      activeNavItemId: state.activeNavItemId,
      filteredNavigation: state.filteredNavigation,
      filteredVerticalSections: state.filteredVerticalSections,
      abilities: state.abilities,
      abilitiesLoaded: state.abilitiesLoaded,
      rxLiteMenu: state.rxLiteMenu,
      sectionVisibility: state.sectionVisibility,

      showHideRxClaimLiteClaims: state.showHideRxClaimLiteClaims,
      showHideRxClaimLiteMember: state.showHideRxClaimLiteMember,

      displayUser: state.displayUser,
      userAliasName: state.userAliasName,
      liteMemRbac: state.liteMemRbac,
      liteClaimsRbac: state.liteClaimsRbac,

      tenantId: state.tenantId,
      currentProgram: state.currentProgram,
      prestoWindow: state.prestoWindow,
      tenantIds: state.tenantIds,
      failedTenants: state.failedTenants,
      tenantStack: state.tenantStack,
      currentTenant: state.currentTenant,

      accounts: state.accounts,
      currentAccount: state.currentAccount,

      platforms: state.platforms,
      currentPlatform: state.currentPlatform,
      platformTenantMap: state.platformTenantMap,

      logoSrc: state.logoSrc,

      providerResults: state.providerResults,
      operationResults: state.operationResults,
      modalOpen: state.modalOpen,
      switchKind: state.switchKind,
      pendingSwitchValue: state.pendingSwitchValue,
      isPriceFrame: state.isPriceFrame,
      programData: state.programData,
      latestHelp: state.latestHelp,

      // Action creators (memoized above)
      dispatch,
      navigateToLanding,
      navigateToSection,
      navigateToComponent,
      updateTenant,
      setCurrentTenant,
      setTenantIds,
      setAccounts,
      setCurrentAccount,
      setPlatforms,
      setCurrentPlatform,
      setPlatformTenantMap,
      setUser,
      setAbilities,
      setAbilitiesLoaded,
      setFilteredNavigation,
      setFilteredVerticalSections,
      setMenuVisibility,
      setActiveNavItem,
      setCurrentProgram,
      setProgramData,
      setLatestHelp,
      openHelp,
      setRxLiteMenu,
      setLiteRbac,
      setPrestoWindow,
      pushTenantStack,
      popTenantStack,
      openSwitchModal,
      closeModal,
      checkLandingPageAccess,
      setHome,
      openPresto,
      getContextString,
      getContextStringLite,
    } as ApplicationContainerAPI;
  }, [
    // state fields
    state.activeView,
    state.activeNavItemId,
    state.filteredNavigation,
    state.filteredVerticalSections,
    state.abilities,
    state.abilitiesLoaded,
    state.rxLiteMenu,
    state.sectionVisibility,
    state.showHideRxClaimLiteClaims,
    state.showHideRxClaimLiteMember,
    state.displayUser,
    state.userAliasName,
    state.liteMemRbac,
    state.liteClaimsRbac,
    state.tenantId,
    state.currentProgram,
    state.prestoWindow,
    state.tenantIds,
    state.failedTenants,
    state.tenantStack,
    state.currentTenant,
    state.accounts,
    state.currentAccount,
    state.platforms,
    state.currentPlatform,
    state.platformTenantMap,
    state.logoSrc,
    state.providerResults,
    state.operationResults,
    state.modalOpen,
    state.switchKind,
    state.pendingSwitchValue,
    state.isPriceFrame,
    state.programData,
    state.latestHelp,
    // actions / callbacks
    dispatch,
    navigateToLanding,
    navigateToSection,
    navigateToComponent,
    updateTenant,
    setCurrentTenant,
    setTenantIds,
    setAccounts,
    setCurrentAccount,
    setPlatforms,
    setCurrentPlatform,
    setPlatformTenantMap,
    setUser,
    setAbilities,
    setAbilitiesLoaded,
    setFilteredNavigation,
    setFilteredVerticalSections,
    setMenuVisibility,
    setActiveNavItem,
    setCurrentProgram,
    setProgramData,
    setLatestHelp,
    openHelp,
    setRxLiteMenu,
    setLiteRbac,
    setPrestoWindow,
    pushTenantStack,
    popTenantStack,
    openSwitchModal,
    closeModal,
    checkLandingPageAccess,
    setHome,
    openPresto,
    getContextString,
    getContextStringLite,
  ]);

  return <ApplicationContext.Provider value={contextValue}>{children}</ApplicationContext.Provider>;
};

/**
 * useApplicationContext - Hook to access application state and actions
 * 
 * Must be used within an ApplicationProvider.
 * 
 * Usage: Destructure only the properties you need
 *   const { activeView, abilities, navigateToLanding } = useApplicationContext();
 * 
 * @throws Error if used outside of ApplicationProvider
 */
export const useApplicationContext = (): ApplicationContainerAPI => {
  const context = useContext(ApplicationContext);
  if (!context) {
    throw new Error("useApplicationContext must be used within ApplicationProvider");
  }
  return context;
};
